import React, {useMemo, useState} from 'react';
import {HiDownload, HiX} from 'react-icons/hi';
import {BsFileEarmarkExcel, BsFileEarmarkPdf} from 'react-icons/bs';
import SkewLoader from "react-spinners/SkewLoader";
import PayrollCompanyLayout from "../../../components/layouts/PayrollCompanyLayout";
import HumanResourceLayout from "../../../components/layouts/HumanResourceLayout";
import useUserStore from "../../../state-management/useUserStore";
import useCompanyStore from '../../../state-management/useCompanyStore';
import {toast} from 'react-toastify';
import API from '../../../services/utils/api';
import {useGetEmployee} from "../../../services/employee.service";

interface Report {
	id: string;
	name: string;
	description: string;
	format: 'PDF' | 'Excel';
	lastGenerated?: string;
	apiType: 'master' | 'newHire' | 'exit' | 'leavesTaken' | 'leavesBalance' | 'employeeLeavesTaken';
}

interface DateRangeModal {
	isOpen: boolean;
	reportId: string;
	reportName: string;
}

const Reports = () => {
	const {userType} = useUserStore();
	const LAYOUT = useMemo(() => {
		switch (userType) {
			case "ORGANIZATION":
				return PayrollCompanyLayout;
			default:
				return HumanResourceLayout;
		}
	}, [userType]);

	const [sortBy, setSortBy] = useState('department');
	const[orderBy, setOrderBy] = useState('createdAt:desc');
	const {currentCompany} = useCompanyStore();
	const companyId = currentCompany?.id;
	const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
	const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

	const { data: employeePaginator, isLoading: loadingEmployees } = useGetEmployee({
		limit: 500,
	});

    const employees = (employeePaginator?.data ?? []).filter(emp => emp.status === 'ACTIVE');

	const [downloadingReports, setDownloadingReports] = useState<Set<string>>(new Set());
	const [dateRangeModal, setDateRangeModal] = useState<DateRangeModal>({
		isOpen: false,
		reportId: '',
		reportName: ''
	});

	// Date fields for different report types
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [createdAtGte, setCreatedAtGte] = useState('');
	const [createdAtLte, setCreatedAtLte] = useState('');
	const [startDateGte, setStartDateGte] = useState('');
	const [startDateLte, setStartDateLte] = useState('');
	const [returnDateGte, setReturnDateGte] = useState('');
	const [returnDateLte, setReturnDateLte] = useState('');

	const reports: Report[] = [
		{
			id: "employee-master-list",
			name: "Employee Master List",
			description: "Complete list of employees grouped by department",
			format: "PDF",
			apiType: "master"
		},
		{
			id: "new-hire",
			name: "New Hire Report",
			description: "Employees hired recently",
			format: "PDF",
			apiType: "newHire"
		},
		{
			id: "employee-exit",
			name: "Employee Exit Report",
			description: "Employees who have left the company",
			format: "PDF",
			apiType: "exit"
		},
		{
			id: "leaves-taken",
			name: "Leaves Taken Report",
			description: "Report of all leaves taken by employees",
			format: "PDF",
			apiType: "leavesTaken"
		},
		{
			id: "leaves-balance",
			name: "Leaves Balance Report",
			description: "Current leave balances for all employees",
			format: "PDF",
			apiType: "leavesBalance"
		},
		{
			id: "employee-leaves-taken",
			name: "Employee Leaves Taken Report",
			description: "Individual employee leave report",
			format: "PDF",
			apiType: "employeeLeavesTaken"
		},
	];

	const setDefaultSortBy = (reportId: string) => {
		switch (reportId) {
			case 'employee-master-list':
				setSortBy('department');
				break;
			case 'new-hire':
				setSortBy('employmentDate');
				break;
			case 'leaves-taken':
				setOrderBy('createdAt:desc');
				break;
			default:
				setSortBy('department');
		}
	};


    const openDateRangeModal = (reportId: string, reportName: string) => {
        if (!companyId) {
            toast.error('Please select a company first');
            return;
        }

        setDateRangeModal({
            isOpen: true,
            reportId,
            reportName
        });

        // Reset all date fields to empty
        setStartDate('');
        setEndDate('');
        setCreatedAtGte('');
        setCreatedAtLte('');
        setStartDateGte('');
        setStartDateLte('');
        setReturnDateGte('');
        setReturnDateLte('');

        setDefaultSortBy('department');
        setOrderBy('createdAt:desc');
    };

	const closeDateRangeModal = () => {
		setDateRangeModal({
			isOpen: false,
			reportId: '',
			reportName: ''
		});
		// Reset all fields
		setStartDate('');
		setEndDate('');
		setCreatedAtGte('');
		setCreatedAtLte('');
		setStartDateGte('');
		setStartDateLte('');
		setReturnDateGte('');
		setReturnDateLte('');
		setSelectedEmployeeId(null);
	};

	const validateFields = () => {
		const reportId = dateRangeModal.reportId;

		switch (reportId) {
			case 'employee-master-list':
				// Only needs sortBy, no validation needed
				return true;

			case 'employee-leaves-taken':
				if (!selectedEmployeeId) {
					toast.error('Please select an employee');
					return false;
				}
				return true;

			case 'new-hire':

				if (new Date(startDate) > new Date(endDate)) {
					toast.error('Recorded from date cannot be after recorded to date');
					return false;
				}
				return true;

			case 'employee-exit':

				if (new Date(startDate) > new Date(endDate)) {
					toast.error('Termination from date cannot be after termination to date');
					return false;
				}
				return true;

			case 'leaves-taken':
				// At least one date range should be provided
				const hasCreatedAt = createdAtGte && createdAtLte;
				const hasStartDate = startDateGte && startDateLte;
				const hasReturnDate = returnDateGte && returnDateLte;



				// Validate individual date ranges
				if (hasCreatedAt && new Date(createdAtGte) > new Date(createdAtLte)) {
					toast.error('Created from date cannot be after created to date');
					return false;
				}
				if (hasStartDate && new Date(startDateGte) > new Date(startDateLte)) {
					toast.error('Start from date cannot be after start to date');
					return false;
				}
				if (hasReturnDate && new Date(returnDateGte) > new Date(returnDateLte)) {
					toast.error('Return from date cannot be after return to date');
					return false;
				}
				return true;

			case 'leaves-balance':
				// No validation needed
				return true;

			default:
				return true;
		}
	};

	const handleDownload = async () => {
		if (!validateFields()) {
			return;
		}

		const report = reports.find(r => r.id === dateRangeModal.reportId);
		if (!report) return;

		console.log('Current sortBy value:', sortBy);
		setDownloadingReports(prev => new Set(prev).add(dateRangeModal.reportId));

		try {
			let response;

			switch (report.apiType) {
				case 'master':
					console.log('Sending sortBy to API:', sortBy);
					response = await API.PayrollAPI.getEmployeeMasterListPdf(companyId!, sortBy);
					break;
				case 'newHire':
					response = await API.PayrollAPI.getNewHirePdf(companyId!, startDate, endDate);
					break;
				case 'exit':
					response = await API.PayrollAPI.getEmployeeExitPdf(companyId!, startDate, endDate);
					break;
				case 'leavesTaken':
					const leavesParams = {
						...(createdAtGte && createdAtLte && { 'createdAt.gte': createdAtGte, 'createdAt.lte': createdAtLte }),
						...(startDateGte && startDateLte && { 'startDate.gte': startDateGte, 'startDate.lte': startDateLte }),
						...(returnDateGte && returnDateLte && { 'returnDate.gte': returnDateGte, 'returnDate.lte': returnDateLte }),

					};
					response = await API.PayrollAPI.getLeavesTakenPdf(companyId!, createdAtGte, createdAtLte, startDateGte, startDateLte, returnDateGte, returnDateLte);
					break;
				case 'leavesBalance':
					response = await API.PayrollAPI.getLeavesBalancePdf(companyId!);
					break;
				case 'employeeLeavesTaken':
					const employeeLeavesParams = {
						...(createdAtGte && createdAtLte && { 'createdAt.gte': createdAtGte, 'createdAt.lte': createdAtLte }),
						...(startDateGte && startDateLte && { 'startDate.gte': startDateGte, 'startDate.lte': startDateLte }),
						...(returnDateGte && returnDateLte && { 'returnDate.gte': returnDateGte, 'returnDate.lte': returnDateLte }),

					};
					response = await API.PayrollAPI.getLeavesTakenEmployeePdf(
						companyId!,
						selectedEmployeeId ?? undefined,
						createdAtGte, createdAtLte, startDateGte, startDateLte, returnDateGte, returnDateLte
					);
					break;
			}

			if (typeof response?.data === "string") {
				window.open(response.data, '_blank');
				toast.success(`${dateRangeModal.reportName} downloaded successfully`);
				closeDateRangeModal();
			} else {
				toast.error('Failed to generate report URL');
			}
		} catch (err) {
			console.error('Failed to download report', err);
			toast.error(`Failed to download ${dateRangeModal.reportName}`);
		} finally {
			setDownloadingReports(prev => {
				const newSet = new Set(prev);
				newSet.delete(dateRangeModal.reportId);
				return newSet;
			});
		}
	};

	const getFormatIcon = (format: string) => {
		return format === 'PDF' ?
			<BsFileEarmarkPdf className="text-red-500 text-lg"/> :
			<BsFileEarmarkExcel className="text-green-500 text-lg"/>;
	};

	const renderModalFields = () => {
		const reportId = dateRangeModal.reportId;

		switch (reportId) {
			case 'employee-master-list':
				return (
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Sort By
						</label>
						<select
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
						>
							<option value="department">Department</option>
							<option value="name">Name</option>
							<option value="employeeNumber">Employee Number</option>
							<option value="employmentDate">Employment Date</option>
						</select>
					</div>
				);

			case 'new-hire':
				return (
					<>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Recorded From
							</label>
							<input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Recorded To
							</label>
							<input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
							/>
						</div>

					</>
				);

			case 'employee-exit':
				return (
					<>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Termination Date From
							</label>
							<input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Termination Date To
							</label>
							<input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
							/>
						</div>
					</>
				);

			case 'leaves-taken':
				return (
					<>
						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Created Date Range (Optional)</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={createdAtGte}
										onChange={(e) => setCreatedAtGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={createdAtLte}
										onChange={(e) => setCreatedAtLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>

						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Start Date Range (Optional)
							</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={startDateGte}
										onChange={(e) => setStartDateGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={startDateLte}
										onChange={(e) => setStartDateLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>

						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Return Date Range (Optional)</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={returnDateGte}
										onChange={(e) => setReturnDateGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={returnDateLte}
										onChange={(e) => setReturnDateLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>


					</>
				);

			case 'leaves-balance':
				return (
					<div className="text-center py-4">
						<p className="text-sm text-gray-600">
							This report doesn't require any additional parameters.
						</p>
					</div>
				);

			case 'employee-leaves-taken':
				return (
					<>
						{/* Employee Selection */}
						<div className="border-b border-gray-200 pb-4 mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Select Employee *
							</label>
							<div className="relative">
								<select
									value={selectedEmployeeId || ''}
									onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null)}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
									disabled={loadingEmployees}
								>
									<option value="">
										{loadingEmployees ? 'Loading employees...' : 'Select an employee'}
									</option>
									{employees.map((employee) => (
										<option key={employee.id} value={employee.id}>
											{employee.firstName} {employee.lastName}
										</option>
									))}
								</select>
							</div>
							{loadingEmployees && (
								<p className="text-xs text-gray-500 mt-1">Loading employees...</p>
							)}
						</div>

						{/* Date Range Fields - Same as leaves-taken */}
						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Created Date Range (Optional)</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={createdAtGte}
										onChange={(e) => setCreatedAtGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={createdAtLte}
										onChange={(e) => setCreatedAtLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>

						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Start Date Range (Optional)</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={startDateGte}
										onChange={(e) => setStartDateGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={startDateLte}
										onChange={(e) => setStartDateLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>

						<div className="border-b border-gray-200 pb-4 mb-4">
							<h4 className="text-sm font-medium text-gray-700 mb-2">Return Date Range (Optional)</h4>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-gray-600 mb-1">From</label>
									<input
										type="date"
										value={returnDateGte}
										onChange={(e) => setReturnDateGte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs text-gray-600 mb-1">To</label>
									<input
										type="date"
										value={returnDateLte}
										onChange={(e) => setReturnDateLte(e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
									/>
								</div>
							</div>
						</div>
					</>
				);

			default:
				return null;
		}
	};

	return (
		<LAYOUT>
			<div className="w-full">
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-gray-800 mb-2">Reports</h1>
					<p className="text-gray-600">Download and view company reports</p>
				</div>

				<div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="bg-gray-50 border-b border-gray-200">
							<tr>
								<th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
									Report Name
								</th>
								<th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
									Description
								</th>
								<th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
									Format
								</th>
								<th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
									Action
								</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{reports.map((report) => (
								<tr key={report.id} className="hover:bg-gray-50">
									<td className="px-6 py-4">
										<div className="text-sm font-medium text-gray-900">
											{report.name}
										</div>
									</td>
									<td className="px-6 py-4">
										<div className="text-sm text-gray-600">
											{report.description}
										</div>
									</td>
									<td className="px-6 py-4">
										<div className="flex items-center space-x-2">
											{getFormatIcon(report.format)}
											<span className="text-sm text-gray-700">
                                                {report.format}
                                            </span>
										</div>
									</td>

									<td className="px-6 py-4 text-center">
										<button
											onClick={() => openDateRangeModal(report.id, report.name)}
											disabled={!companyId}
											className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
												!companyId
													? 'bg-gray-100 text-gray-400 cursor-not-allowed'
													: 'bg-primary-blue text-white hover:opacity-90'
											}`}
										>
											<HiDownload className="text-lg"/>
											<span>Download</span>
										</button>
									</td>
								</tr>
							))}
							</tbody>
						</table>
					</div>
				</div>

				{reports.length === 0 && (
					<div className="bg-white rounded-lg border border-gray-300 p-8 text-center">
						<p className="text-gray-500">No reports available</p>
					</div>
				)}

				{!companyId && (
					<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
						<p className="text-yellow-800 text-sm">
							Please select a company to download reports
						</p>
					</div>
				)}

				{/* Dynamic Modal */}
				{dateRangeModal.isOpen && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
						<div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-semibold text-gray-900">
									Report Parameters
								</h3>
								<button
									onClick={closeDateRangeModal}
									className="text-gray-400 hover:text-gray-600"
								>
									<HiX className="text-xl"/>
								</button>
							</div>

							<div className="mb-6">
								<p className="text-sm text-gray-600 mb-4">
									Configure parameters for <strong>{dateRangeModal.reportName}</strong>
								</p>

								<div className="space-y-4">
									{renderModalFields()}
								</div>
							</div>

							<div className="flex justify-end space-x-3">
								<button
									onClick={closeDateRangeModal}
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
								>
									Cancel
								</button>
								<button
									onClick={handleDownload}
									disabled={downloadingReports.has(dateRangeModal.reportId)}
									className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
										downloadingReports.has(dateRangeModal.reportId)
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-primary-blue text-white hover:opacity-90'
									}`}
								>
									{downloadingReports.has(dateRangeModal.reportId) ? (
										<div className="flex items-center space-x-2">
											<SkewLoader size={16} color="#9CA3AF"/>
											<span>Downloading...</span>
										</div>
									) : (
										'Download Report'
									)}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</LAYOUT>
	);
};

export default Reports;

